import {
	type ArticleWidth,
	applyArticleScreenWidth,
	resolveArticleWidth,
	snapArticleWidth,
	toggleArticleWidth,
	useArticleWidthStore,
} from "@core-ui/ContextServices/ArticleWidth/ArticleWidthStore";
import { useTouchHandler } from "@core-ui/hooks/useTouchHandler";
import { type KeyboardEvent, useRef, useState } from "react";
import { useArticleWidth } from "./useArticleDimensions";

const KEYBOARD_STEP_PX = 40;

const readNormalWidth = () =>
	parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--article-max-width"));

const widthForKey = (key: string, current: number, normal: number, available: number): ArticleWidth | null => {
	switch (key) {
		case "ArrowLeft":
			return snapArticleWidth(current - KEYBOARD_STEP_PX, normal, available);
		case "ArrowRight":
			return snapArticleWidth(current + KEYBOARD_STEP_PX, normal, available);
		case "Home":
			return "normal";
		case "End":
			return "full";
		case "Enter":
			return toggleArticleWidth(current, normal);
		default:
			return null;
	}
};

interface Drag {
	startX: number;
	startWidth: number;
	next: ArticleWidth;
}

export const useArticleWidthResize = () => {
	const available = useArticleWidth();
	const width = useArticleWidthStore((state) => state.width);
	const setWidth = useArticleWidthStore((state) => state.setWidth);
	const [isResizing, setIsResizing] = useState(false);
	const dragRef = useRef<Drag>(null);
	const frameRef = useRef<number>(null);

	// Measured only after the layout has measured the column, which also keeps server rendering off `document`.
	const normal = available ? readNormalWidth() : 0;
	const current = resolveArticleWidth(width, normal, available);

	const previewWidth = (next: ArticleWidth) => {
		dragRef.current.next = next;
		if (frameRef.current !== null) return;
		frameRef.current = requestAnimationFrame(() => {
			frameRef.current = null;
			if (dragRef.current) applyArticleScreenWidth(dragRef.current.next);
		});
	};

	const { onPointerDown, onTouchStart, onMouseDown } = useTouchHandler({
		onStart: (clientX) => {
			dragRef.current = { startX: clientX, startWidth: current, next: width };
			setIsResizing(true);
		},
		onMove: (_deltaX, _deltaY, clientX) => {
			const drag = dragRef.current;
			if (!drag) return;
			// The column is centred, so each edge moves by half the change and the handle stays under the pointer.
			previewWidth(snapArticleWidth(drag.startWidth + 2 * (clientX - drag.startX), normal, available));
		},
		onEnd: () => {
			const drag = dragRef.current;
			dragRef.current = null;
			if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
			frameRef.current = null;
			setIsResizing(false);
			if (drag) setWidth(drag.next);
		},
	});

	const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
		const next = widthForKey(event.key, current, normal, available);
		if (next === null) return;
		event.preventDefault();
		setWidth(next);
	};

	return {
		isVisible: normal > 0 && available > normal,
		isNormal: current <= normal,
		isResizing,
		range: { min: Math.round(normal), max: Math.round(available), now: Math.round(current) },
		handlers: {
			onPointerDown,
			onTouchStart,
			onMouseDown,
			onDoubleClick: () => setWidth(toggleArticleWidth(current, normal)),
			onKeyDown,
		},
	};
};
